import { useState } from 'preact/hooks';
import { useModel } from '../model.ts';
import { language } from '../../shared/languages.ts';
import { playHash, playBlob, canRecord, Recorder, sha256Hex, voiceLabel, speakWithDevice } from '../audio.ts';
import { Reading } from '../Reading.tsx';
import { toast } from '../toast.ts';

interface Take {
  hash: string;
  blob?: Blob;
}

export function Edit({ id, defaultDeck, onDone }: { id: string | null; defaultDeck: string | null; onDone: () => void }) {
  const m = useModel();
  const existing = id ? m.notes.get(id) : undefined;
  const decks = m.liveDecks();
  const [deckId, setDeckId] = useState(existing?.deckId ?? defaultDeck ?? m.studyDeck() ?? decks[0]?.id ?? '');
  const [text, setText] = useState(existing?.text ?? '');
  const [reading, setReading] = useState(existing?.reading ?? '');
  const [meaning, setMeaning] = useState(existing?.meaning ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [tags, setTags] = useState(existing?.tags.join(', ') ?? '');
  const [takes, setTakes] = useState<Take[]>((existing?.recordings ?? []).map((hash) => ({ hash })));
  const [rec, setRec] = useState<Recorder | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lang = language(m.decks.get(deckId)?.language ?? 'none');
  const audioDeck = lang.voices.length > 0;
  const audioStale = existing && (existing.text.trim() !== text.trim() || existing.deckId !== deckId);

  async function toggleRecord() {
    setError(null);
    if (rec) {
      setRec(null);
      const blob = await rec.stop();
      const hash = await sha256Hex(blob);
      setTakes((t) => [...t.filter((x) => x.hash !== hash), { hash, blob }]);
      void playBlob(blob);
      return;
    }
    try {
      const r = new Recorder();
      await r.start();
      setRec(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function save(e: Event) {
    e.preventDefault();
    if (!deckId) return setError('Pick a deck.');
    if (!meaning.trim()) return setError(`${lang.labels.meaning} is required.`);
    if (!text.trim() && !reading.trim() && !takes.length) return setError(`${lang.labels.text} is required.`);
    // Recordings go into the local store first; sync uploads them before the note.
    for (const t of takes) {
      if (t.blob && !(await m.db.get('audio', t.hash))) {
        await m.db.put('audio', { hash: t.hash, blob: t.blob, mime: t.blob.type || 'audio/webm', uploaded: 0 });
      }
    }
    await m.saveNote({
      id: existing?.id,
      deckId,
      text: text.trim(),
      reading: reading.trim(),
      meaning: meaning.trim(),
      notes: notes.trim(),
      tags: tags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
      recordings: takes.map((t) => t.hash),
    });
    toast(existing ? 'Saved' : 'Added. It will come up as a new card.');
    onDone();
  }

  async function remove() {
    if (!existing) return;
    await m.saveNote({ id: existing.id, deleted: true });
    toast('Deleted', {
      label: 'Undo',
      run: () => void m.saveNote({ id: existing.id, deleted: false }),
    });
    onDone();
  }

  return (
    <form class="edit" onSubmit={save}>
      <h2>{existing ? 'Edit card' : 'Add a card'}</h2>
      {decks.length > 1 && (
        <label>
          Deck
          <select value={deckId} onChange={(e) => setDeckId(e.currentTarget.value)}>
            {decks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        {lang.labels.meaning}
        <input value={meaning} onInput={(e) => setMeaning(e.currentTarget.value)} required />
      </label>
      <label>
        {lang.labels.text}
        {audioDeck && <span class="muted small"> the voices read this</span>}
        <input value={text} onInput={(e) => setText(e.currentTarget.value)} lang={lang.speech || undefined} />
      </label>
      <label>
        {lang.labels.reading}
        {lang.readingStyle === 'tones' && <span class="muted small"> tone number after each syllable</span>}
        <input value={reading} onInput={(e) => setReading(e.currentTarget.value)} autocapitalize="off" spellcheck={false} />
        {reading && lang.readingStyle === 'tones' && (
          <div class="preview">
            <Reading text={reading} tones />
          </div>
        )}
      </label>
      <label>
        Notes
        <textarea value={notes} rows={2} onInput={(e) => setNotes(e.currentTarget.value)} />
      </label>
      <label>
        Tags <span class="muted small">comma separated</span>
        <input value={tags} onInput={(e) => setTags(e.currentTarget.value)} autocapitalize="off" />
      </label>

      <fieldset>
        <legend>Audio</legend>
        {existing && !audioStale && existing.tts.length > 0 && (
          <div class="audio-row wrap">
            {existing.tts.map((c) => (
              <button type="button" key={c.hash} onClick={() => void playHash(c.hash)}>
                ▶ {voiceLabel(c.voice)}
              </button>
            ))}
          </div>
        )}
        {audioDeck && text.trim() && (!existing || audioStale || existing.tts.length === 0) && (
          <p class="muted small">
            The server makes audio from the {lang.labels.text.toLowerCase()} after the next sync.{' '}
            <button type="button" class="link" onClick={() => void speakWithDevice(text, lang.speech)}>
              Try this device's voice
            </button>
          </p>
        )}
        {!audioDeck && !takes.length && (
          <p class="muted small">This deck has no generated voices. You can still record audio for a card.</p>
        )}
        {takes.length > 0 && (
          <>
            <p class="small">Your recordings play instead of the generated voices:</p>
            <div class="audio-row wrap">
              {takes.map((t, i) => (
                <span key={t.hash} class="take">
                  <button type="button" onClick={() => (t.blob ? void playBlob(t.blob) : void playHash(t.hash))}>
                    ▶ Recording {i + 1}
                  </button>
                  <button
                    type="button"
                    class="link"
                    aria-label="Remove recording"
                    onClick={() => setTakes(takes.filter((x) => x.hash !== t.hash))}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </>
        )}
        {canRecord() && (
          <button type="button" class={`mic ${rec ? 'on' : ''}`} onClick={toggleRecord}>
            {rec ? '■ Stop recording' : '● Record audio'}
          </button>
        )}
        {audioDeck && <p class="muted small">A native speaker recorded on your phone beats any synthetic voice.</p>}
      </fieldset>

      {error && <p class="error">{error}</p>}
      <div class="form-actions">
        <button type="submit" class="primary">
          {existing ? 'Save' : 'Add'}
        </button>
        <button type="button" onClick={onDone}>
          Cancel
        </button>
        {existing && (
          <button type="button" class="danger" onClick={remove}>
            Delete
          </button>
        )}
      </div>
    </form>
  );
}
